import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faPencilAlt,
  faPlus,
  faTrash,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { doc, setDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import useSiteData from '../../hooks/useSiteData';

const EditHomeReviews = () => {
  const { data, isLoading: isSiteDataLoading, refetch } = useSiteData();
  const [formData, setFormData] = useState({
    reviews: [
      {
        name: '',
        rating: 5,
        comment: '',
        date: new Date().toISOString().split('T')[0],
        image: 'https://randomuser.me/api/portraits/men/1.jpg'
      }
    ]
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  // Updated useEffect to properly handle the data
  useEffect(() => {
    if (data && Array.isArray(data.reviews)) {
      setFormData({ reviews: data.reviews });
      console.log('Loaded reviews:', data.reviews); // Debug log
    }
  }, [data]);

  const handleReviewChange = (index, field, value) => {
    setFormData(prev => {
      const newReviews = [...prev.reviews];
      newReviews[index] = {
        ...newReviews[index],
        [field]: value
      };
      return { ...prev, reviews: newReviews };
    });
  };

  const addReview = () => {
    setFormData(prev => ({
      ...prev,
      reviews: [
        {
          name: '',
          rating: 5,
          comment: '',
          date: new Date().toISOString().split('T')[0],
          image: 'https://randomuser.me/api/portraits/men/1.jpg'
        },
        ...prev.reviews
      ]
    }));
  };

  const removeReview = (index) => {
    setFormData(prev => ({
      ...prev,
      reviews: prev.reviews.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    try {
      // Filter out empty reviews
      const nonEmptyReviews = formData.reviews.filter(review => 
        review.name.trim() !== '' || 
        review.comment.trim() !== ''
      );

      if (nonEmptyReviews.length === 0) {
        toast.error('Please add at least one review with data before saving');
        return;
      }

      setIsLoading(true);
      const docRef = doc(db, "reviews", "singleton");
      
      // Save with the correct structure
      await setDoc(docRef, {
        reviews: nonEmptyReviews // Changed to match Firebase structure
      });
      
      toast.success('Reviews updated successfully!');
      setError('');
      setIsLoading(false);
      setIsEditing(false);
      refetch();
    } catch (error) {
      console.error('Error updating reviews:', error);
      setError('Failed to update reviews.');
      toast.error('Failed to update reviews.');
      setIsLoading(false);
    }
  };

  const renderReadOnlyMode = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Reviews</h2>
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
          >
            <FontAwesomeIcon icon={faPencilAlt} className="mr-2" />
            Edit
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {formData.reviews.map((review, index) => (
            <div key={index} className="border p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <img 
                  src={review.image} 
                  alt={review.name} 
                  className="w-10 h-10 rounded-full mr-2"
                />
                <span className="font-semibold">{review.name}</span>
                <div className="ml-2 flex">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon 
                      key={i}
                      icon={faStar}
                      className={i < review.rating ? "text-yellow-400" : "text-gray-300"}
                    />
                  ))}
                </div>
              </div>
              <p className="text-gray-600">{review.comment}</p>
              <div className="text-sm text-gray-500 mt-2">{review.date}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {isSiteDataLoading ? (
          <div className="text-center">Loading...</div>
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-6">Edit Reviews</h1>
            
            {error && (
              <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
                {error}
              </div>
            )}
            
            {isEditing ? (
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold">Edit Reviews</h2>
                    <button
                      onClick={addReview}
                      className="px-4 py-2 text-green-600 bg-green-50 rounded-md hover:bg-green-100 flex items-center"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      Add Review
                    </button>
                  </div>

                  <div className="space-y-6">
                    {formData.reviews.map((review, index) => (
                      <div key={index} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-medium">Review #{index + 1}</h3>
                          <button
                            onClick={() => removeReview(index)}
                            className="text-red-500 hover:text-red-700"
                            disabled={formData.reviews.length === 1}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Name
                            </label>
                            <input
                              type="text"
                              value={review.name}
                              onChange={(e) => handleReviewChange(index, 'name', e.target.value)}
                              className="w-full px-3 py-2 border rounded-md"
                              placeholder="Enter reviewer name"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Rating
                            </label>
                            <select
                              value={review.rating}
                              onChange={(e) => handleReviewChange(index, 'rating', Number(e.target.value))}
                              className="w-full px-3 py-2 border rounded-md"
                            >
                              {[5,4,3,2,1].map(num => (
                                <option key={num} value={num}>{num} Stars</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Comment
                            </label>
                            <textarea
                              value={review.comment}
                              onChange={(e) => handleReviewChange(index, 'comment', e.target.value)}
                              className="w-full px-3 py-2 border rounded-md"
                              rows="3"
                              placeholder="Enter review comment"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Date
                            </label>
                            <input
                              type="date"
                              value={review.date}
                              onChange={(e) => handleReviewChange(index, 'date', e.target.value)}
                              className="w-full px-3 py-2 border rounded-md"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-4 sticky bottom-0 bg-gray-50 py-4">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            ) : (
              renderReadOnlyMode()
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EditHomeReviews;
